import { useClient, useQuery } from '@weenat/client'
import {
  DEFAULT_COLD_TEMPERATURE_THRESHOLD_PARAMETERS,
  DEFAULT_HEAT_ACCUMULATION_PARAMETERS,
  isAllowedTimeStepsForInlinedCumulativeSums
} from '@weenat/client/dist/core/cumulative-sums'
import { HistoryPeriod, SURFACE_POWER_THRESHOLD } from '@weenat/client/dist/core/history'
import { TimeSteps } from '@weenat/client/dist/enums/TimeSteps'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import {
  AccumulationMetric,
  GetCumulativeSumResultParams,
  toBackEndCumulativeSumMetric
} from '@weenat/client/dist/resources/cumulativeSum'
import { useIntl } from '@weenat/wintl'
import { isNil } from 'lodash-es'
import { useState } from 'react'
import useDisclosure from '../hooks/useDisclosure'
import Card from '../kit/Card'
import MetricIcons from '../kit/icons/MetricIcons'
import LoadingCircle from '../kit/loaders/LoadingCircle'
import PanelSectionTitle from '../kit/panels/PanelSectionTitle'
import Text from '../kit/Text'
import TextNumber from '../kit/TextNumber'
import MissingDataBadge from '../sums/MissingDataBadge'
import EditPeriodBasedCumulativeSumModal from './EditPeriodBasedCumulativeSumModal'

interface CumulativeSumParameterProps {
  label: string
  value: string
}

const CumulativeSumParameter: FC<CumulativeSumParameterProps> = ({ label, value }) => {
  return (
    <Flex $gap={'sm'} $justifyContent='space-between'>
      <Text $fontSize='sm'>{label}</Text>
      <Text $fontSize='sm' $fontWeight='bold'>
        {value}
      </Text>
    </Flex>
  )
}

interface PeriodBasedCumulativeSumProps {
  metric: AccumulationMetric
  period: HistoryPeriod
  timeStep: TimeSteps
  deviceId: number
}

const PeriodBasedCumulativeSum: FC<PeriodBasedCumulativeSumProps> = ({
  metric,
  period,
  timeStep,
  deviceId
}) => {
  const { t } = useIntl()
  const { convertValue, convertDuration } = useConvertedValue()
  const client = useClient()

  const paramsModalDisclosure = useDisclosure()

  const isIncompatibleWithTimeStep =
    !isAllowedTimeStepsForInlinedCumulativeSums(timeStep) ||
    (metric === 'heatAccumulation' && timeStep !== TimeSteps.perDays) ||
    // See https://linear.app/weenat/issue/APP-5026/bug-erreur-incoherente-sur-oadcumulative-sumresult#comment-53710bc9
    (metric === 'SSI_HOURSAccumulation' && timeStep !== TimeSteps.perDays)

  const [heatAccParams, setHeatAccParams] = useState<{
    base: number | undefined
    gddTMax: number | undefined
  }>(DEFAULT_HEAT_ACCUMULATION_PARAMETERS)

  const [coldHoursParams, setColdHoursParams] = useState(
    DEFAULT_COLD_TEMPERATURE_THRESHOLD_PARAMETERS
  )

  const commonQueryParams: GetCumulativeSumResultParams = {
    since: period.since,
    before: period.before,
    deviceId,
    span: timeStep,
    metrics: [metric]
  }

  const queryParams: GetCumulativeSumResultParams =
    metric === 'heatAccumulation'
      ? {
          ...commonQueryParams,
          ...heatAccParams
        }
      : metric === 'COLD_T_HOUR'
        ? {
            ...commonQueryParams,
            ...coldHoursParams
          }
        : commonQueryParams

  // Stations are volontarily omitted
  // If the user has a station it should have access to the device
  const cumulativeSumQuery = useQuery(client.cumulativeSum.result(queryParams), {
    enabled: !isIncompatibleWithTimeStep
  })

  const { data } = cumulativeSumQuery

  let value: number | undefined = undefined
  let missingDataCount: number | undefined = undefined

  if (!isNil(data)) {
    const { missingDays, sums } = data
    const metricAsBackEndMetric = toBackEndCumulativeSumMetric(metric)
    value = sums[metricAsBackEndMetric]
    missingDataCount = missingDays[metricAsBackEndMetric]
  }

  const name = t(`metrics.${metric}`, { capitalize: true })
  const MetricIcon = MetricIcons[metric]

  return (
    <Card $p={0} $overflow='hidden'>
      {/* Header */}
      <Flex $p={'md'} $gap={'300'} $alignItems='center'>
        <MetricIcon $size='lg' />
        <Text>{name}</Text>
      </Flex>

      {/* Content */}
      {isIncompatibleWithTimeStep ? (
        <Flex
          $flexDirection='column'
          $gap={'sm'}
          $alignItems='center'
          $justifyContent='center'
          $backgroundColor={'grayscale.100'}
          $flex={1}
          $p={'md'}
          $minHeight={96}
        >
          <Text $textAlign='center' $fontSize='sm'>
            {t('help.cumulative_sum_incompatible_with_timestep')}
          </Text>
        </Flex>
      ) : cumulativeSumQuery.isLoading ? (
        <Flex $flexDirection='column' $gap={'sm'} $alignItems='center'>
          <LoadingCircle />
        </Flex>
      ) : (
        <>
          {/* Value */}
          <Flex $flexDirection='column' $gap={'sm'} $p={'md'}>
            <TextNumber $fontSize='lg' $fontWeight='bold'>
              {metric === 'leafWetnessAccumulation'
                ? convertDuration({ value })
                : convertValue({ metric, value, displayUnit: true, showEmptyState: true })}
            </TextNumber>
            {!isNil(missingDataCount) && missingDataCount !== 0 ? (
              <MissingDataBadge
                count={missingDataCount}
                timeUnit={timeStep === TimeSteps.perHours ? 'hours' : 'days'}
              />
            ) : null}
          </Flex>

          {/* Parameters */}
          {metric === 'heatAccumulation' ||
          metric === 'COLD_T_HOUR' ||
          metric === 'SSI_HOURSAccumulation' ? (
            <Flex
              $flexDirection='column'
              $gap={'md'}
              $p={'md'}
              $backgroundColor={'grayscale.100'}
              $flex={1}
            >
              <Flex $gap={'300'} $justifyContent='space-between' $width={'100%'}>
                <PanelSectionTitle>{t('titles.active_settings')}</PanelSectionTitle>
                {metric === 'heatAccumulation' || metric === 'COLD_T_HOUR' ? (
                  <>
                    <Text $underlined $fontSize='sm' onClick={paramsModalDisclosure.open}>
                      {t('actions.edit', { capitalize: true })}
                    </Text>
                    <EditPeriodBasedCumulativeSumModal
                      width={400}
                      isOpen={paramsModalDisclosure.isOpen}
                      close={paramsModalDisclosure.close}
                      onConfirm={(newParams) => {
                        if ('coldTemperatureThreshold' in newParams) {
                          setColdHoursParams(newParams)
                        } else {
                          setHeatAccParams(newParams)
                        }
                        paramsModalDisclosure.close()
                      }}
                      initialParams={metric === 'COLD_T_HOUR' ? coldHoursParams : heatAccParams}
                    />
                  </>
                ) : null}
              </Flex>

              {metric === 'heatAccumulation' ? (
                <>
                  <CumulativeSumParameter
                    label={t('models.alert.fields.cumulative_sum_dd_min_threshold.label', {
                      capitalize: true
                    })}
                    value={convertValue({
                      metric: 'T',
                      displayUnit: true,
                      value: heatAccParams.base
                    })}
                  />
                  <CumulativeSumParameter
                    label={t('models.alert.fields.cumulative_sum_dd_max_threshold.label', {
                      capitalize: true
                    })}
                    value={convertValue({
                      metric: 'T',
                      displayUnit: true,
                      value: heatAccParams.gddTMax
                    })}
                  />
                </>
              ) : metric === 'COLD_T_HOUR' ? (
                <CumulativeSumParameter
                  label={t('help.cumulative_sum_create_form_cold_threshold_label')}
                  value={convertValue({
                    metric: 'T',
                    displayUnit: true,
                    value: coldHoursParams.coldTemperatureThreshold
                  })}
                />
              ) : metric === 'SSI_HOURSAccumulation' ? (
                <CumulativeSumParameter
                  label={t('models.alert.fields.threshold.label', { capitalize: true })}
                  value={convertValue({
                    metric: 'SSI',
                    displayUnit: true,
                    value: SURFACE_POWER_THRESHOLD
                  })}
                />
              ) : null}
            </Flex>
          ) : null}
        </>
      )}
    </Card>
  )
}

export default PeriodBasedCumulativeSum
